import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { fetchTurnosPacienteData } from "./turnosPacienteService";
import { procesarSeleccionFiltros, prepararDatosComprobante } from "./turnosPacienteUtils";
import { filtrarTurnos } from "./turnosPacienteUtils";
import { cancelarTurno, getTurnosPaciente } from "../../apiClient/rutes/turnos";
import { sedesDisponibles, opcionesFiltros } from "./turnosPacienteUtils";

export const useTurnosPaciente = (mostrarNotificacion) => {
  // Estados principales
  const [usuario, setUsuario] = useState({});
  const [cargando, setCargando] = useState(false);
  const [urlAxioPP, setUrlAxio] = useState("");
  
  // Estados de datos
  const [servicios, setServicios] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [estudios, setEstudios] = useState([]);
  const [listadoPrestadores, setListadoPrestadores] = useState([]);
  
  // Estados de filtros
  const [sedeSeleccionada, setSedeSeleccionada] = useState(-1);
  const [especialidadSeleccionada, setEspecialidadSeleccionada] = useState(-1);
  const [prestadorSeleccionado, setPrestadorSeleccionado] = useState(-1);
  const [filtroCancelados, setFiltroCancelados] = useState(-1);
  const [filtroAsistidos, setFiltroAsistidos] = useState(-1);
  const [estadoTurnosFiltrados, setEstadoTurnosFiltrados] = useState(-1);
  
  // Estados de turnos
  const [listadoTurnos, setListadoTurnos] = useState([]);
  const [listadoTurnosFiltrados, setListadoTurnosFiltrados] = useState([]);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState({});
  
  // Estados de modales
  const [abrirModalCancelarReserva, setAbrirModalCancelarReserva] = useState(false);
  const [abrirComprobanteTurno, setAbrirComprobanteTurno] = useState(false);
  const [abrirDesplegableAsistidos, setAbrirDeplegableAsistidos] = useState(false);
  const [abrirDesplegableCancelados, setAbrirDeplegableCancelados] = useState(false);
  
  // Estados adicionales
  const [expandedAcordeons, setExpandedAcordeons] = useState(new Set());
  const [openReagendar, setOpenReagendar] = useState(true);
  const [turnoReagendar, setTurnoReagendar] = useState(null);

  const history = useHistory();

  // Función para cargar todos los datos iniciales
  const cargarDatos = async (sesion) => {
    setCargando(true);
    try {
      const resultado = await fetchTurnosPacienteData(sesion);
      
      if (resultado.success) {
        const { turnos, prestadores, especialidades, estudios, servicios } = resultado.data;
        
        setListadoTurnos(turnos);
        setPrestadores(prestadores);
        setListadoPrestadores(prestadores);
        setEspecialidades(especialidades);
        setEstudios(estudios);
        setServicios(servicios);
      } else {
        mostrarNotificacion(true, resultado.error, "rojo");
      }
    } catch (error) {
      mostrarNotificacion(true, "Error al cargar datos: " + error.message, "rojo");
    }
    setCargando(false);
  };

  // Efecto de carga inicial
  useEffect(() => {
    const inicializar = async () => {
      try {
        const sesion = JSON.parse(sessionStorage.getItem("ppUL"));
        if (sesion) {
          await cargarDatos(sesion);
          setUsuario(sesion);
          setUrlAxio(localStorage.getItem("urlAxioPP"));
          document.title = localStorage.getItem("tituloWeb");
        }
      } catch (e) {
        history.push({ pathname: "/ErrorPage" });
      }
    };
    inicializar();
  }, [history, mostrarNotificacion]);

  // Efecto para filtrar turnos cuando cambian los filtros
  useEffect(() => {
    const turnosFiltrados = filtrarTurnos(listadoTurnos, {
      especialidadSeleccionada,
      prestadorSeleccionado,
      filtroAsistidos,
      filtroCancelados,
      estadoTurnosFiltrados
    });
    setListadoTurnosFiltrados(turnosFiltrados);
  }, [
    listadoTurnos,
    especialidadSeleccionada,
    prestadorSeleccionado,
    filtroAsistidos,
    filtroCancelados,
    estadoTurnosFiltrados
  ]);

  // Handler para cambios en filtros
  const handleChangeSelect = (value, select) => {
    try {
      if (select === "Cancelados") {
        setFiltroCancelados(value);
        return;
      }
      
      if (select === "Asistidos") {
        setFiltroAsistidos(value);
        return;
      }

      const resultado = procesarSeleccionFiltros(value, select, prestadores, usuario);
      
      // Aplicar cambios según el tipo de selección
      if (resultado.listadoPrestadores !== undefined) {
        setListadoPrestadores(resultado.listadoPrestadores);
      }
      if (resultado.prestadorSeleccionado !== undefined) {
        setPrestadorSeleccionado(resultado.prestadorSeleccionado);
      }
      if (resultado.sedeSeleccionada !== undefined) {
        setSedeSeleccionada(resultado.sedeSeleccionada);
      }
      if (resultado.especialidadSeleccionada !== undefined) {
        setEspecialidadSeleccionada(resultado.especialidadSeleccionada);
      }
    } catch (error) {
      mostrarNotificacion(true, error.message, "rojo");
    }
  };

  // Handler para cancelar turno
  const handleClickCancelarReserva = async () => {
    setAbrirModalCancelarReserva(false);
    setCargando(true);
    
    try {
      const resultado = await cancelarTurno(usuario.token, turnoSeleccionado.codigo);
      mostrarNotificacion(true, resultado.mensaje, resultado.success ? "verde" : "rojo");
      
      if (resultado.success) {
        // Recargar turnos después de cancelar
        const turnosActualizados = await getTurnosPaciente(usuario.token, usuario.codigo, "");
        setListadoTurnos(turnosActualizados);
      }
    } catch (error) {
      mostrarNotificacion(true, "Error inesperado: " + error.message, "rojo");
    }
    setCargando(false);
  };

  // Handlers de modales
  const handleClickSeleccionarTurno = (turno) => {
    setTurnoSeleccionado(turno);
    setAbrirModalCancelarReserva(true);
  };

  const handleClickComprobanteTurno = (turno) => {
    const datosComprobante = prepararDatosComprobante(turno, usuario, servicios);
    setTurnoSeleccionado(datosComprobante);
    setAbrirComprobanteTurno(true);
  };

  const handleClickReservarTurnoPrestador = (prestador) => {
    history.push({
      pathname: "/portal/Reservar turno",
      state: { prestador: prestador },
    });
  };

  const handleClickReagendarTurno = (turno) => {
    setTurnoReagendar({
      ...turno,
      nombre_profesional: turno.prestadorNom,
      idTramite: turno.codigo,
    });
    setTurnoSeleccionado(turno);
    setOpenReagendar(true);
  };

  // Toggles para modales
  const toggleModalCancelarReserva = () => {
    if (abrirModalCancelarReserva) {
      setTurnoSeleccionado({});
    }
    setAbrirModalCancelarReserva(!abrirModalCancelarReserva);
  };

  const toggleComprobanteTurno = () => {
    if (abrirComprobanteTurno) {
      setTurnoSeleccionado({});
    }
    setAbrirComprobanteTurno(!abrirComprobanteTurno);
  };

  // Return - valores necesarios para el componente
  return {
    // Estados principales
    usuario,
    cargando,
    listadoTurnos,
    listadoTurnosFiltrados,
    turnoSeleccionado,
    
    // Estados de datos
    servicios,
    especialidades,
    prestadores,
    estudios,
    listadoPrestadores,
    
    // Estados de filtros
    sedeSeleccionada,
    especialidadSeleccionada,
    prestadorSeleccionado,
    filtroCancelados,
    filtroAsistidos,
    estadoTurnosFiltrados,
    
    // Estados de modales
    abrirModalCancelarReserva,
    abrirComprobanteTurno,
    abrirDesplegableAsistidos,
    abrirDesplegableCancelados,
    expandedAcordeons,
    openReagendar,
    turnoReagendar,
    
    // Setters
    setEstadoTurnosFiltrados,
    setAbrirModalCancelarReserva,
    setAbrirComprobanteTurno,
    setExpandedAcordeons,
    
    // Handlers
    handleChangeSelect,
    handleClickSeleccionarTurno,
    handleClickCancelarReserva,
    handleClickComprobanteTurno,
    handleClickReservarTurnoPrestador,
    handleClickReagendarTurno,
    toggleModalCancelarReserva,
    toggleComprobanteTurno,
    
    // Datos estáticos
    sedes: sedesDisponibles,
    cancelados: opcionesFiltros.cancelados,
    asistidos: opcionesFiltros.asistidos,
  };
};
